import React, { useState, useEffect } from 'react';
import { FiSearch, FiSave, FiCreditCard, FiPlus, FiFileText } from 'react-icons/fi';
import NewCard from '../components/previlagecard/NewCard';
import axios from 'axios';
import { GET_PRIVILAGE_CARD, BASE_URL, GET_PRIVILAGE_HEAD, POST_PRIVILAGE_CARD_PRICING } from '../utlilities/config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import CardList from '../components/previlagecard/CardList';

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

const ItemPrivilegeCard = () => {
  const [loading, setLoading] = useState(false);
  const [cardHeaders, setCardHeaders] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [discountData, setDiscountData] = useState({});
  const [cardModal, setCardModal] = useState(false);
  const [cardListModal, setCardlistModal] = useState(false);
  const [initialDiscountData, setInitialDiscountData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [changes, setChanges] = useState(0);

  const token = localStorage.getItem('accessToken');

  useEffect(() => {
    fetchHeaderData();
  }, []);

  useEffect(() => {
    fetchPrivilegeCard();
    // Reset changes and discount data when component mounts
    setChanges(0);
    setDiscountData({});
    setInitialDiscountData({});
    setSearchQuery('');
  }, []);

  useEffect(() => {
    if (cardHeaders.length > 0 && itemData.length > 0) {
      initializeDiscountData(itemData);
    }
  }, [cardHeaders, itemData]);

  const fetchHeaderData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_PRIVILAGE_HEAD}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });
      if (response.status === 200) {
        setCardHeaders(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching header data:', error);
      toast.error('Failed to load card data');
    }
  };

  const fetchPrivilegeCard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}${GET_PRIVILAGE_CARD}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'filterType': 'ITEM'
        },
      });
      if (response.status === 200) {
        setItemData(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching privilege card data:', error);
      toast.error('Failed to load item data');
    } finally {
      setLoading(false);
    }
  };

  const initializeDiscountData = (itemData) => {
    const initialData = {};
    
    // Default type for items is 'I'
    const defaultType = 'I';
    
    itemData.forEach(item => {
      cardHeaders.forEach(card => {
        const cardName = card.CardName;
        // Get Type from API response, fallback to 'I' for items
        const itemType = item.Type || defaultType;
        // Include Type in key to differentiate items with same ID but different types
        const key = `${card.CardID}_${item['Test ID']}_${itemType}`;
        initialData[key] = {
          CardID: card.CardID,
          TestID: item['Test ID'],
          Type: itemType, // Store Type from API response (I for items)
          Discount: item[cardName] !== null ? item[cardName] : 0
        };
      });
    });
  
    // Set both initial data and discount data
    setDiscountData(initialData);
    setInitialDiscountData(initialData);
  };
  
  const handleDiscountChange = (cardID, testID, itemType, value) => {
    // Include Type in key to differentiate items with same ID but different types
    const key = `${cardID}_${testID}_${itemType}`;
    let newValue = value === '' ? '' : parseFloat(value);
    
    // Prevent negative values
    if (newValue < 0) newValue = 0;
    
    setDiscountData(prevData => ({
      ...prevData,
      [key]: {
        ...prevData[key],
        Discount: newValue
      }
    }));
    
    // Count changes for the status indicator
    const isChange = newValue !== initialDiscountData[key]?.Discount;
    if (isChange) {
      setChanges(prev => prev + 1);
    }
  };
  
  const handleSave = async () => {
    // Default type for items is 'I'
    const defaultType = 'I';
    
    // Create a new object with only the changed data
    const changedData = Object.keys(discountData).filter(key => {
      // Compare the current discount with the initial value
      return discountData[key].Discount !== initialDiscountData[key]?.Discount;
    }).map(key => {
      // Key format: cardID_testID_type
      const [cardID, testID, type] = key.split('_');
      return {
        CardID: parseInt(cardID, 10),
        TestID: parseInt(testID, 10),
        Type: type || discountData[key].Type || defaultType, // Include Type in the payload
        Discount: discountData[key].Discount,
      };
    });
    
    if (changedData.length === 0) {
      toast.info('No changes to save');
      return;
    }
  
    // Only send the changed data
    const postData = {
      PrivilegeCardsRates: changedData
    };

    console.log(postData);
  
    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}${POST_PRIVILAGE_CARD_PRICING}`, postData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });
  
      if (response.status === 200) {
        toast.success('Discounts updated successfully');
        fetchHeaderData();
        fetchPrivilegeCard();
        setChanges(0);
      }
    } catch (error) {
      toast.error('Error updating discounts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search term
  const filteredItemData = itemData.filter(item => {
    const searchLower = searchQuery.toLowerCase().trim();
    
    // Search across multiple fields: Test Name (Item Name), TestCode, and Test ID
    const matchesSearch = searchLower === '' || 
      (item['Test Name']?.toLowerCase().includes(searchLower)) ||
      (item.TestCode?.toLowerCase().includes(searchLower)) ||
      (item['Test ID']?.toString().includes(searchLower));
    
    return matchesSearch;
  });
  
  useEffect(() => {
    const handleWheel = (e) => {
      // Check if the target is a number input
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      // Check if the target is a number input
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    // Add event listeners
    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    // Cleanup
    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  // Filter out cards that have a DiscPercentage
  const displayableCardHeaders = cardHeaders.filter(card => card.DiscPercentage === null);

  return (
    <div className="flex h-screen bg-background flex-col overflow-hidden">
      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {loading && <Spinner />}
      <div className='flex-1 flex h-screen flex-col overflow-hidden hide-scrollbar'>
      <div className="bg-white p-3 mb-2">
        <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                Item Privilege Card Management
              </h1>
              <p className="text-xs text-black">Manage privilege card discounts for items</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Manage Cards Button */}
              <button
                  onClick={() => setCardlistModal(true)}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                >
                <FiCreditCard className="mr-2" /> Manage Cards
              </button>
                
              <button
                onClick={() => setCardModal(true)}
                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              >
                <FiPlus className="mr-2" /> Add New Card
              </button>
              <button 
                onClick={handleSave}
                className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg shadow-md flex items-center 
                  ${changes > 0 
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:ring-indigo-300'
                    : 'bg-gray-500 hover:bg-gray-600 focus:ring-gray-300'
                  } transition-all duration-200 hover:shadow-lg focus:ring-4`}
              >
                <FiSave className="mr-2" /> 
                Save Changes
              </button>

            </div>
          </div>
        </div>
      
        <div className="p-3 flex-1 flex flex-col h-screen overflow-hidden">
          
          {/* Filters and Actions Section */}
          <div className="bg-white rounded-xl shadow-md p-3 mb-3">
            <div className="flex flex-wrap justify-between items-center gap-4">
              {/* Search */}
              <div className="relative w-full md:w-96">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                  <FiSearch size={18} />
                </div>
                <input
                  type="text"
                  placeholder="Search items by name or ID..."
                  className="w-full pl-10 pr-3 py-1 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {/* Status Bar */}
            <div className="mt-4 flex items-center text-sm text-black">
              <FiFileText className="mr-2" />
              <span>
                {filteredItemData.length} items found
                {searchQuery && ` matching "${searchQuery}"`}
              </span>
            </div>
          </div>
          
          {/* Table Section */}
          <div className="bg-white rounded-xl shadow-md flex-1 overflow-hidden">
            <div className="h-full overflow-x-auto overflow-y-auto">
              {filteredItemData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-black">
                  <FiSearch className="mb-3" size={32} />
                  <p className="text-lg font-medium">No items found</p>
                  <p className="text-sm mt-1">Try changing your search criteria</p>
                </div>
              ) : (
                <div className="overflow-x-auto h-full">
                  <table className="min-w-full border-collapse" style={{ width: 'max-content' }}>
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="sticky left-0 z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-r" style={{ minWidth: '250px' }}>
                          <div className="flex items-center gap-1">
                            <span>Item Name</span>
                          </div>
                        </th>
                        <th className="sticky left-[250px] z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-r" style={{ minWidth: '100px' }}>
                          <div className="flex items-center gap-1">
                            <span>Rate</span>
                          </div>
                        </th>
                        {displayableCardHeaders.map(card => (
                          <th key={card.CardID} className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-r whitespace-nowrap" style={{ minWidth: '150px' }}>
                            <div className="flex items-center gap-1">
                              <span className="whitespace-nowrap">{card.CardName} (%)</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItemData.map((item, index) => {
                        // Create unique key by combining Test ID and Type to avoid duplicate key warnings
                        const defaultType = 'I';
                        const uniqueKey = `${item['Test ID']}_${item.Type || defaultType}`;
                        return (
                          <tr 
                            key={uniqueKey}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >

                          <td className={`sticky left-0 z-10 px-6 py-3 text-sm text-black border-b border-r ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={{ minWidth: '250px' }}>
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                              {item['Test Name']}
                            </div>
                          </td>


                          <td className={`sticky left-[250px] z-10 px-6 py-3 text-sm font-medium border-b border-r whitespace-nowrap ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={{ minWidth: '100px' }}>
                            <div className="flex items-center">
                              <span className="text-black">₹{item.Rate || 0}</span>
                            </div>
                          </td>
                          {displayableCardHeaders.map(card => {
                            // Get Type from item data, fallback to 'I' for items
                            const defaultType = 'I';
                            const itemType = item.Type || defaultType;
                            // Include Type in key to differentiate items with same ID but different types
                            const key = `${card.CardID}_${item['Test ID']}_${itemType}`;
                            const initialValue = initialDiscountData[key]?.Discount;
                            const currentValue = discountData[key]?.Discount;
                            const isChanged = initialValue !== currentValue;
                            
                            return (
                              <td key={key} className="px-6 py-3 text-sm border-b border-r" style={{ minWidth: '150px' }}>
                                <input
                                  type="number"
                                  min="0"
                                  className={`w-full py-2 px-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${
                                    isChanged 
                                      ? 'border-indigo-300 focus:border-indigo-500 focus:ring-indigo-200 bg-indigo-50' 
                                      : 'border-gray-200 focus:border-gray-400 focus:ring-gray-100 bg-white'
                                  }`}
                                  value={currentValue !== undefined ? currentValue : ''}
                                  onChange={(e) => handleDiscountChange(card.CardID, item['Test ID'], itemType, e.target.value)}
                                />
                              </td>
                            );
                          })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {cardModal && (
        <NewCard setCardModal={setCardModal} fetchData={fetchHeaderData} fetchData1={fetchPrivilegeCard} />
      )}

      {cardListModal && (
        <CardList setCardlistModal={setCardlistModal} fetchData={fetchHeaderData} fetchData1={fetchPrivilegeCard} cardslist={cardHeaders}/>
      )}
    </div>
  );
};

export default ItemPrivilegeCard;

